import React, { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { PlusIcon, SearchIcon } from 'lucide-react';
import { useStudio } from '../../contexts/StudioContext';
import { Booking, BookingStatus } from '../../types/booking';
import { euro, shortDate, timeRange } from '../../utils/format';
import { BookingDetail } from '../../components/admin/BookingDetail';
import { NewBookingDialog } from '../../components/admin/NewBookingDialog';
import { PaymentBadge, StatusBadge } from '../../components/admin/StatusBadge';
const filters: {
  id: BookingStatus | 'all';
  label: string;
}[] = [{
  id: 'all',
  label: 'Alles'
}, {
  id: 'pending',
  label: 'Aanvragen'
}, {
  id: 'confirmed',
  label: 'Bevestigd'
}, {
  id: 'completed',
  label: 'Afgerond'
}, {
  id: 'cancelled',
  label: 'Geannuleerd'
}];
export function BookingsAdmin() {
  const {
    bookings,
    services
  } = useStudio();
  const [filter, setFilter] = useState<BookingStatus | 'all'>('all');
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState<Booking | null>(null);
  const [creating, setCreating] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return bookings.filter(b => filter === 'all' ? true : b.status === filter).filter(b => q === '' ? true : [b.reference, b.customer.name, b.customer.car, b.customer.plate, b.customer.phone].join(' ').toLowerCase().includes(q)).sort((a, b) => a.date + a.time < b.date + b.time ? 1 : -1);
  }, [bookings, filter, query]);
  return <div className="w-full max-w-full px-4 py-5 lg:px-8 lg:py-8" data-id="element-859" data-mp="1kiixc3yz46mi">
      <div className="flex items-center justify-between gap-3" data-id="element-860" data-mp="1fmv49dhdetbq">
        <p className="text-sm text-dim" data-id="element-861" data-mp="1amz11gghz5x8">
          {rows.length} van {bookings.length}
        </p>
        <button type="button" onClick={() => setCreating(true)} className="inline-flex items-center gap-2 rounded-full bg-ember px-4 py-2.5 text-sm font-semibold text-void transition-[background-color,box-shadow] duration-200 ease-smooth hover:bg-white hover:glow" data-id="element-862" data-mp="16kc5b4gph0ur">
          <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-863" data-mp="mzls6x7srgka" />
          Nieuw
        </button>
      </div>

      <div className="mt-4 space-y-3" data-id="element-864" data-mp="rxdhdjh24oks">
        <div className="relative" data-id="element-865" data-mp="ryf2yhvp8tgr">
          <SearchIcon className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-dim" aria-hidden="true" data-id="element-866" data-mp="1fnzp19n5g6u9" />
          <label htmlFor="search" className="sr-only" data-id="element-867" data-mp="1jrfixy7u3t39">
            Zoek op naam, kenteken of referentie
          </label>
          <input id="search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Naam, kenteken, referentie" className="w-full rounded-2xl border border-edge bg-carbon py-3.5 pl-11 pr-4 text-sm text-chalk placeholder:text-dim/60 focus:border-ember" data-id="element-868" data-mp="1ax16e3eouwnu" />
        </div>

        <label htmlFor="status-filter" className="sr-only" data-id="element-869" data-mp="2cqt11xspghcb">
          Filter op status
        </label>
        <select id="status-filter" value={filter} onChange={e => setFilter(e.target.value as BookingStatus | 'all')} className="w-full rounded-2xl border border-edge bg-carbon px-4 py-3.5 text-sm text-chalk focus:border-ember lg:hidden" data-id="element-870" data-mp="28q0td8zheki5">
          {filters.map(f => <option key={f.id} value={f.id} data-id="element-871" data-mp="1tvcgvavnylt1">
              {f.label}
            </option>)}
        </select>

        <div className="hidden gap-2 lg:flex" data-id="element-872" data-mp="23rfsd7cenwsn">
          {filters.map(f => <button key={f.id} type="button" onClick={() => setFilter(f.id)} className={`rounded-full border px-4 py-2 text-sm transition-colors duration-200 ease-smooth ${filter === f.id ? 'border-ember bg-ember text-void' : 'border-edge bg-carbon text-chalk hover:border-ember/50'}`} data-id="element-873" data-mp="23pcu6013mxcu">
              {f.label}
            </button>)}
        </div>
      </div>

      <ul className="mt-4 space-y-2.5 lg:hidden" data-id="element-874" data-mp="1orbv1sneg708">
        {rows.map(booking => {
        const service = services.find(s => s.id === booking.serviceId);
        return <li key={booking.id} data-id="element-875" data-mp="1xri492c2ektl">
              <button type="button" onClick={() => setOpen(booking)} className="w-full rounded-2xl border border-edge bg-carbon p-4 text-left active:border-ember" data-id="element-876" data-mp="22pjnbn9hqk52">
                <span className="flex items-start justify-between gap-3" data-id="element-877" data-mp="2cg4mbbt2tqoi">
                  <span className="min-w-0" data-id="element-878" data-mp="2heo89c7o72gv">
                    <span className="block truncate font-medium text-chalk" data-id="element-879" data-mp="2hdmydb2e4m0q">{booking.customer.name}</span>
                    <span className="mt-0.5 block truncate text-sm text-dim" data-id="element-880" data-mp="2lhkure0bziq3">{service?.name}</span>
                  </span>
                  <span className="shrink-0 font-display text-base font-extrabold text-chalk" data-id="element-881" data-mp="2qci5qqh6825m">
                    {euro(booking.amount)}
                  </span>
                </span>
                <span className="mt-3 flex items-center justify-between gap-2" data-id="element-882" data-mp="9uit48zww9y2">
                  <span className="truncate text-xs text-dim" data-id="element-883" data-mp="5rcm2z4z6eqj">
                    {shortDate(booking.date)} · {booking.time}
                  </span>
                  <StatusBadge status={booking.status} data-id="element-884" data-mp="xhqtccihgst8" />
                </span>
              </button>
            </li>;
      })}
        {rows.length === 0 && <li className="rounded-2xl border border-dashed border-edge px-5 py-8 text-sm text-dim" data-id="element-885" data-mp="tirikba2m1tv">
            Geen boekingen gevonden.
          </li>}
      </ul>

      <div className="mt-5 hidden overflow-hidden rounded-2xl border border-edge bg-carbon lg:block" data-id="element-886" data-mp="jq2kqj7g0mh0">
        <table className="w-full text-left text-sm" data-id="element-887" data-mp="1h7j3qip8tjx5">
          <thead className="border-b border-edge text-xs uppercase tracking-[0.14em] text-dim" data-id="element-888" data-mp="1ceq05y5ighmn">
            <tr data-id="element-889" data-mp="1vbrrrno7f55s">
              <th scope="col" className="px-5 py-3.5 font-normal" data-id="element-890" data-mp="1vb8famhbp8z5">
                Datum
              </th>
              <th scope="col" className="px-5 py-3.5 font-normal" data-id="element-891" data-mp="209jmepg8h5do">
                Klant
              </th>
              <th scope="col" className="px-5 py-3.5 font-normal" data-id="element-892" data-mp="1mieuyan0rjef">
                Behandeling
              </th>
              <th scope="col" className="px-5 py-3.5 font-normal" data-id="element-893" data-mp="1qiyqxavuwgyz">
                Status
              </th>
              <th scope="col" className="px-5 py-3.5 font-normal" data-id="element-894" data-mp="1cn3e9ms1w241">
                Betaling
              </th>
              <th scope="col" className="px-5 py-3.5 text-right font-normal" data-id="element-895" data-mp="1hhqtsw52tm6h">
                Totaal
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-edge" data-id="element-896" data-mp="17myu9mrxop92">
            {rows.map(booking => {
            const service = services.find(s => s.id === booking.serviceId);
            return <tr key={booking.id} onClick={() => setOpen(booking)} className="cursor-pointer transition-colors duration-200 ease-smooth hover:bg-panel" data-id="element-897" data-mp="31yx49qy2hp60">
                  <td className="px-5 py-4" data-id="element-898" data-mp="mcum5a2n7pu5">
                    <span className="block text-chalk" data-id="element-899" data-mp="1024u8f08hrrf">{shortDate(booking.date)}</span>
                    <span className="block text-xs text-dim" data-id="element-900" data-mp="vboeupcurw2f">
                      {timeRange(booking.time, booking.durationMinutes)}
                    </span>
                  </td>
                  <td className="px-5 py-4" data-id="element-901" data-mp="1a2f4pruvwl2w">
                    <span className="block text-chalk" data-id="element-902" data-mp="152aps69mjyes">{booking.customer.name}</span>
                    <span className="block text-xs text-dim" data-id="element-903" data-mp="1j1seymqu1doo">
                      {booking.customer.car}
                      {booking.customer.plate ? ` · ${booking.customer.plate}` : ''}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-dim" data-id="element-904" data-mp="3kt5hj55bv9hk">{service?.name}</td>
                  <td className="px-5 py-4" data-id="element-905" data-mp="3fulx596du5rb">
                    <StatusBadge status={booking.status} data-id="element-906" data-mp="3fvn5h5w0ke5c" />
                  </td>
                  <td className="px-5 py-4" data-id="element-907" data-mp="2uxmfb7urbt4z">
                    <PaymentBadge status={booking.paymentStatus} data-id="element-908" data-mp="2yz815ip9rhrb" />
                  </td>
                  <td className="px-5 py-4 text-right font-display font-extrabold text-chalk" data-id="element-909" data-mp="33q6gaeyiu9sz">
                    {euro(booking.amount)}
                  </td>
                </tr>;
          })}
          </tbody>
        </table>
        {rows.length === 0 && <p className="px-5 py-10 text-sm text-dim" data-id="element-910" data-mp="2c0tlnahogqa4">Geen boekingen gevonden.</p>}
      </div>

      {open && <BookingDetail booking={open} onClose={() => setOpen(null)} data-id="element-911" data-mp="2g0vqttmmg1u5" />}

      {creating && <NewBookingDialog date={format(new Date(), 'yyyy-MM-dd')} onClose={() => setCreating(false)} onCreated={ref => {
      setToast(`Afspraak ${ref} ingepland.`);
      window.setTimeout(() => setToast(null), 4000);
    }} data-id="element-912" data-mp="2ky4014pskvj9" />}

      {toast && <p role="status" className="fixed bottom-28 left-1/2 z-[70] -translate-x-1/2 rounded-full border border-ember/50 bg-carbon px-5 py-3 text-sm text-chalk lg:bottom-8" data-id="element-913" data-mp="5anfpshm0jus">
          {toast}
        </p>}
    </div>;
}